import { Alert, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Pagination, Paper, SelectChangeEvent, Table, TableBody, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StyledSelect from "../common/StyledSelect";
import { useSearchParams } from "react-router-dom";
import Search from "../common/Search";
import Loader from "../common/Loader";
import {
	useReactTable,
	getCoreRowModel,
	ColumnDef,
	flexRender,
	getSortedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFacetedMinMaxValues,
	getExpandedRowModel,
	FilterFn,
	SortingFn,
	sortingFns,
	Row,
	SortingState,
} from "@tanstack/react-table";
import {
	RankingInfo,
	rankItem,
	compareItems,
} from "@tanstack/match-sorter-utils";
import StyledTableCell from "../common/StyledTableCell";
import ComplianceStatus from "../common/ComplianceStatus";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { useListTaggedResources } from "../../hooks/useListTaggedResources";
import awsServicesDisplay from "./awsServices";

declare module "@tanstack/table-core" {
	interface FilterFns {
		fuzzy: FilterFn<unknown>;
	}
	interface FilterMeta {
		itemRank: RankingInfo;
	}
}

interface ViewResourcesProps {
	arn: string;
	region: string;
	listViews: any;
	selectedView: string;
	setSelectedView: (index: string) => void;
}

type ControlFinding = {
  control_ref: string;
  status: string;
};

type TagData = {
	Key: string;
	Value: string;
}

type PropertiesProp = {
	Data: TagData[];
	LastReportedAt: string;
	Name: string;
}
interface ResourceRow {
	Arn: string;
	Identifier: string;
	Service: string;
	ResourceType: string;
	Region: string;
	OwningAccountId: string;
	ControlFindings: ControlFinding[];
	Properties: PropertiesProp;
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
	// Rank the item
	try {
		const itemRank = rankItem(row.getValue(columnId), value);
		// Store the itemRank info
		addMeta({
			itemRank,
		});

			// Return if the item should be filtered in/out
		return itemRank.passed;
	} catch (error) {
		return false;
	}
};

const fuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
	let dir = 0;

	// Only sort by rank if the column has ranking information
	if (rowA.columnFiltersMeta[columnId]) {
		dir = compareItems(
			rowA.columnFiltersMeta[columnId]?.itemRank!,
			rowB.columnFiltersMeta[columnId]?.itemRank!
		);
	}

	// Provide an alphanumeric fallback for when the item ranks are equal
	return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

enum Compliance {
  FAILED,
  PASSED,
  UNKNOWN,
}

const complianceFuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
  if (rowA.original[columnId][0].status === rowB.original[columnId][0].status) {
    return 0;
  }

  return Compliance[rowA.original[columnId][0].status] > Compliance[rowB.original[columnId][0].status]
	? 1
	: -1;
};

const ViewTaggedResources: FC<ViewResourcesProps> = ({
	arn,
	region,
	listViews,
	selectedView,
	setSelectedView,
}: ViewResourcesProps) => {
	const { resources, loading, error } = useListTaggedResources({ arn, region, selectedView });

	const [open, setOpen] = useState(false);

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleView = (e: any) => {
    setSelectedView(e.target.value);
		setTableRows([]);
	}

	// States for the table
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(20);
	const [rowSelection, setRowSelection] = useState({});
	const [tableRows, setTableRows] = useState([]);
	const [sorting, setSorting] = useState<SortingState>([
		{
			id: "ControlFindings",
			desc: false,
		},
	]);

	let [searchParams, setSearchParams] = useSearchParams({
		Identifier: "",
		Service: "",
		ResourceType: "",
		Region: "",
		OwningAccountId: "",
		ControlFindings: [],
		search: "",
		page: "1",
	});

	const updateSearch = useCallback(
		(search: string) => {
			setSearchParams({
				Identifier: searchParams.get("Identifier") || "",
				Service: searchParams.get("Service") || "",
				ResourceType: searchParams.get("ResourceType") || "",
				Region: searchParams.get("Region") || "",
				OwningAccountId: searchParams.get("OwningAccountId") || "",
				ControlFindings: searchParams.get("ControlFindings") || "",
				search,
			});
		},
		[searchParams]
	);
	
	const columns = useMemo<ColumnDef<ResourceRow>[]>(() => [
		{
			id: "Identifier",
			header: ({ table }) => {
				return (
					<Box>
						<Typography fontSize={"14px"}>{"Identifier"}</Typography>
					</Box>
				);
			},
			accessorKey: "Identifier", //using accessorKey dot notation to access nested data
			filterFn: "fuzzy",
			sortingFn: fuzzySort,
		},
		{
			id: "ResourceType",
			header: ({ table }) => {
				return (
					<Box>
						<Typography fontSize={"14px"}>{"Resource Type"}</Typography>
					</Box>
				);
			},
			accessorKey: "ResourceType", //using accessorKey dot notation to access nested data
			filterFn: "fuzzy",
			sortingFn: fuzzySort,
		},
		{
			id: "Region",
			header: ({ table }) => {
				return (
					<Box>
						<Typography fontSize={"14px"}>{"Region"}</Typography>
					</Box>
				);
			},
			accessorKey: "Region", //using accessorKey dot notation to access nested data
			filterFn: "fuzzy",
			sortingFn: fuzzySort,
		},
		{
			id: "OwningAccountId",
			header: ({ table }) => {
				return (
					<Box>
						<Typography fontSize={"14px"}>{"Owner Account Id"}</Typography>
					</Box>
				);
			},
			accessorKey: "OwningAccountId", //using accessorKey dot notation to access nested data
			filterFn: "fuzzy",
			sortingFn: fuzzySort,
		},
		{
			id: "Data",
			header: ({ table }) => {
				return (
					<Box>
						<Typography align="center" fontSize={"14px"}>{"Tags"}</Typography>
					</Box>
				);
			},
			accessorKey: "Properties.Data", //using accessorKey dot notation to access nested data
			filterFn: "fuzzy",
			sortingFn: fuzzySort,
			cell: ({ row }: any) => {
				return (
					<Box>
						<Typography align="center" fontSize={"14px"}>{row.original.Properties.Data.length}</Typography>
					</Box>
				);
			},
		},
		{
			id: "ControlFindings",
			header: ({ table }) => {
				return (
					<Box>
						<Typography fontSize={"14px"}>{"Security Hub Status"}</Typography>
					</Box>
				);
			},
			accessorKey: "ControlFindings", //using accessorKey dot notation to access nested data
			filterFn: "fuzzy",
			sortingFn: complianceFuzzySort,
			cell: ({ row, getValue }: any) => {
				let value = getValue();
				return (
					<>
					{value.map((control: any) => {
						if(control.control_ref !== "N/A"){
							return (
								<ComplianceStatus value={control.status}></ComplianceStatus>
							)
						}	else {
							return (
								<Box>
									<Typography align="center" fontSize={"14px"}>N/A</Typography>
								</Box>	
							)
						}
					})}
					</>
				);
			},
		},
		{
			id: "expander",
			header: ({ table }) => {
				return <Box />;
			},
			cell: ({ row }: { row: Row<ResourceRow> }) => {
				return row.getCanExpand() ? (
					<IconButton
						key={row.id}
						onClick={row.getToggleExpandedHandler()}
						sx={{
								cursor: "pointer",
						}}
						aria-label="delete"
						size="small"
				>
						{row.getIsExpanded() ? (
							<ExpandLessOutlinedIcon fontSize="inherit" color="secondary" />
						) : (
							<ExpandMoreOutlinedIcon fontSize="inherit" color="secondary" />
						)}
					</IconButton>
				) : (
					""
				);
			},
		},
	],[]);

	const table = useReactTable({
	data: tableRows,
	columns,
	filterFns: {
		fuzzy: fuzzyFilter,
	},
	defaultColumn: {
		size: 1,
		minSize: 1,
		maxSize: 2,
	},
	initialState: {
		pagination: {
			pageSize: 20,
			pageIndex: 0,
		},
	},
	state: {
		sorting,
		rowSelection,
		pagination: {
			pageIndex,
			pageSize,
		},
	},
	getRowCanExpand: (row: Row<ResourceRow>) => {
		return true;
	},
	onSortingChange: setSorting,
	onRowSelectionChange: setRowSelection,
	getCoreRowModel: getCoreRowModel(),
	getFilteredRowModel: getFilteredRowModel(),
	getSortedRowModel: getSortedRowModel(),
	getPaginationRowModel: getPaginationRowModel(),
	getFacetedRowModel: getFacetedRowModel(),
	getFacetedUniqueValues: getFacetedUniqueValues(),
	getFacetedMinMaxValues: getFacetedMinMaxValues(),
	getExpandedRowModel: getExpandedRowModel(),
	debugTable: true,
});

	useEffect(() => {
		let resourcedata: any = resources;
		const searchValue = searchParams.get("search");
	if (searchValue !== null && searchValue !== "") {
		const search = searchValue.toLowerCase();
		resourcedata = resourcedata.filter((resource: ResourceRow) => {
			if (resource?.Identifier.toLowerCase().includes(search)) return true;
			else if (resource?.Service.toLowerCase().includes(search)) return true;
			else if (resource?.ResourceType.toLowerCase().includes(search)) return true;
			else if (resource?.Region.toLowerCase().includes(search)) return true;
			else if (resource?.OwningAccountId.toLowerCase().includes(search)) return true;
			else if (resource?.ControlFindings?.some(finding => 
				finding.control_ref.toLowerCase().includes(search) ||
				finding.status.toLowerCase().includes(search)
			)) return true;
			else if (resource?.Properties?.Data?.some(data => 
				data.Key.toLowerCase().includes(search) ||
				data.Value.toLowerCase().includes(search)
			)) return true;
			else return false;
		});
	}
		setRowSelection({})
		setTableRows(resourcedata);
	}, [searchParams, resources]);
	
	if (error) {
		return (
			<Typography
				component={"span"}
				variant="h6"
				color="text.primary"
				paragraph
			>
				loading...
			</Typography>
		);
	}

	const ResourceDropDown: FC<any> = ({
		row,
	}: {
		row: Row<ResourceRow>;
	}) => {
		return (
			<>
			<TableRow key={`${row.id}-expanded`}>
				<StyledTableCell key={`f-2`} colSpan={12} sx={{ paddingLeft: 0 }}>
					<Box display="flex">
						<Box sx={{ width: '30%' }}>Arn</Box>
						<Box sx={{ width: '70%' }}>{row.original.Arn}</Box>
					</Box>
				</StyledTableCell>
			</TableRow>
			<TableRow key={`${row.id}-expanded-2`}>
					<StyledTableCell key={`f-2-2`} colSpan={12} sx={{ paddingLeft: 0 }}>
						<Box display="flex">
							<Box sx={{ width: '30%' }}>Service</Box>
							<Box sx={{ width: '70%' }}>{row.original.Service}</Box>
						</Box>
					</StyledTableCell>
			</TableRow>
			{row.original.ControlFindings[0].control_ref !== "N/A" && (
				<>
					<TableRow key={`${row.id}-expanded-3-header`}>
						<StyledTableCell colSpan={12}>
							<Box sx={{ display: 'flex', fontWeight: 'bold', border: 1, padding: 1 }}>
								<Box sx={{ width: '30%' }}>Control Name</Box>
								<Box sx={{ width: '70%' }}>Compliance Status</Box>
							</Box>
							{row.original.ControlFindings.map((control: any, index: number) => (
								<Box sx={{ display: 'flex', padding: 1, borderBottom: 1, borderLeft: 1, borderRight: 1 }}>
									<Box sx={{ width: '30%' }}>{control.control_ref}</Box>
									<Box sx={{ width: '70%' }}>
										<ComplianceStatus value={control.status} />
									</Box>
								</Box>
							))}
						</StyledTableCell>
					</TableRow>
				</>
			)}
			{row.original.Properties.Data.length !== 0 && (
				<>
					<TableRow key={`${row.id}-expanded-3-header`}>
						<StyledTableCell colSpan={12}>
							<Box sx={{ display: 'flex', fontWeight: 'bold', border: 1, padding: 1 }}>
								<Box sx={{ width: '30%' }}>Key</Box>
								<Box sx={{ width: '70%' }}>Value</Box>
							</Box>
							{ row.original.Properties.Data.map((data: any) => (
								<Box sx={{ display: 'flex', padding: 1, borderBottom: 1, borderLeft: 1, borderRight: 1 }}>
									<Box sx={{ width: '30%' }}>{data.Key}</Box>
									<Box sx={{ width: '70%' }}>{data.Value}</Box>
								</Box>
							))}
						</StyledTableCell>
					</TableRow>
				</>
			)}
		</>
		);
	};

	return (
		<Container maxWidth="lg" component={Paper}>
			<Grid
				container
				sx={{ pb: 2, pt: 2 }}
				spacing={2}
				alignItems="stretch"
				flexDirection={"column"}
			>
				<Grid container flexDirection="row">
					<Grid item xs md={12}>
						<Typography
							component={"span"}
							variant="h6"
							color="text.primary"
							paragraph
						>
							Resource Explorer Tagged Resources
						</Typography>
					</Grid>	
				</Grid>
				<Grid container flexDirection="row">
					<Paper
						key={"1"}
						component="div"
						sx={{
							p: "0px 0px",
							mb: 0,
							display: "flex",
							border: 0,
							boxShadow: 0,
							alignSelf: "center"
						}}
					>
						<Search
							globalFilter={searchParams.get("search") || ""}
							setGlobalFilter={updateSearch}
						></Search>
						<IconButton onClick={handleClickOpen} color="secondary"><InfoIcon color="info" /></IconButton>
					</Paper>
					<Paper
						key={"2"}
						component="div"
						elevation={0}
						sx={{
							p: "2px 4px",
							mb: 2,
							display: "flex",
							justifyContent: "flex-end",
							flexGrow: 1,
						}}
					>
						<Button
							id="aws-resource-explorer-console-btn"
							type="submit"
							variant="outlined"
							endIcon={<OpenInNewIcon />}
							href="//console.aws.amazon.com/resource-explorer/home"
							target="_blank"
							sx={{
								width: 350,
								height: 40,
								"&:hover": {
									color: "primary.main"
								}
							}}
						>
							View AWS Resource Explorer
						</Button>
					</Paper>
				</Grid>
				<Grid container flexDirection="row">
					<Paper
						key={"1"}
						component="div"
						sx={{
							p: "0px 0px",
							mb: 0,
							display: "flex",
							border: 0,
							boxShadow: 0,
							alignSelf: "center"
						}}
					>
						<Typography
							component={"span"}
							variant="h6"
							color="text.primary"
							fontWeight={400}
							fontSize={"16px"}
							sx={{
								m: 1,
								ml: 0,
								mt: 0,
								mb: 3,
							}}
						>
							{`Showing ${
								table.getState().pagination.pageIndex *
									table.getState().pagination.pageSize +
								1
							}-${
								(table.getState().pagination.pageIndex + 1) *
									table.getState().pagination.pageSize <
								tableRows.length
								? (table.getState().pagination.pageIndex + 1) *
									table.getState().pagination.pageSize
								: tableRows.length
							} of ${tableRows.length} entries`}
						</Typography>
					</Paper>
					<Paper
						key={"2"}
						component="div"
						elevation={0}
						sx={{
							p: "2px 4px",
							mb: 2,
							display: "flex",
							justifyContent: "flex-end",
							flexGrow: 1,
						}}
					>
						<InputLabel id="list-view" sx={{ alignSelf: "center", p: 1 }}>
							Select a Resource Explorer User View
						</InputLabel>
						<FormControl sx={{ minWidth: 130 }} size="small">
							<StyledSelect
								labelId="list-view-select-label"
								id="list-view-select"
								value={selectedView}
								onChange={handleView}
							>
								{selectedView === "loading" && (
									<MenuItem value="loading" disabled>
										Loading...
									</MenuItem>
								)}
								{listViews?.items?.map((a: any) => (
									<MenuItem key={a.ViewArn} value={a.ViewArn}>
										{a.Name}
									</MenuItem>
								))}
							</StyledSelect>
						</FormControl>
					</Paper>
					<Paper
						key={"3"}
						component="div"
						elevation={0}
						sx={{
							p: "2px 4px",
							mb: 2,
							display: "flex",
							justifyContent: "flex-end",
						}}
					>
						<InputLabel id="page-size" sx={{ alignSelf: "center", p: 1 }}>
							Show
						</InputLabel>
						<FormControl sx={{ minWidth: 130 }} size="small">
							<StyledSelect
								labelId="page-size-select-label"
								id="page-size-select"
								value={table.getState().pagination.pageSize}
								onChange={(e: SelectChangeEvent<unknown>) => {
									setPageSize(Number(e.target.value));
								}}
							>
								<MenuItem value={20}>20</MenuItem>
								<MenuItem value={50}>50</MenuItem>
								<MenuItem value={100}>100</MenuItem>
							</StyledSelect>
						</FormControl>
					</Paper>
				</Grid>
				{
					resources?.length === 0 ? (
						<Grid container flexDirection="row" sx={{pb: 2}}>
							<Alert variant="outlined" severity="info">
								After you initially turn on Resource Explorer in an AWS Region, 
								it can take up to 36 hours for indexing and replication to the aggregator index to complete.
							</Alert>
						</Grid>
					): null
				}
				<Grid container flexDirection="row">
					<Table
						sx={{
							width: '100%',
						}}
					>
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<StyledTableCell
												key={header.id}
												colSpan={header.colSpan}
											>
												{header.isPlaceholder ? null : (
													<Box
														sx={{
															display: "flex",
															whiteSpace: "pre-wrap",
														}}
													>
														<span>
															{flexRender(
																header.column.columnDef.header,
																header.getContext()
															)}
														</span>
													</Box>
												)}
											</StyledTableCell>
										);
									})}
								</TableRow>
							))}
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<StyledTableCell colSpan={table.getTotalSize()}>
										<Loader></Loader>
									</StyledTableCell>
								</TableRow>
							) : (
								table.getRowModel().rows.map((row) => {
									return (
										<>
											<TableRow key={row.id}>
												{row.getVisibleCells().map((cell) => {
													return (
														<StyledTableCell
															key={cell.id}
														>
															{flexRender(
																cell.column.columnDef.cell,
																cell.getContext()
															)}
														</StyledTableCell>
													);
												})}
											</TableRow>
											{row.getIsExpanded() && (
												<ResourceDropDown
													row={row}
												/>
											)}
										</>
									);
								})
							)}
						</TableBody>
					</Table>
				</Grid>
				<Grid
					container
					flexDirection="row"
					justifyContent={"flex-end"}
					sx={{ p: 0, pt: 2 }}
				>
					<Pagination
						count={table.getPageCount()}
						variant="outlined"
						sx={{
							color: "primary.main",
							borderColor: "primary.main",
						}}
						shape="rounded"
						page={table.getState().pagination.pageIndex + 1}
						onChange={(event: React.ChangeEvent<unknown>, value: number) => {
							setPageIndex(value - 1);
						}}
					/> 
				</Grid>
			</Grid>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{"We support the following services"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{awsServicesDisplay.map((service) => (
							<Typography>
								{service}
							</Typography>
						))}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Close</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};

export default ViewTaggedResources;